import type { CartItem } from '@/hooks/useCartContext';

export const BUSINESS_WHATSAPP = '5511952451307';

const money = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** "2026-12-25" → "25/12/2026" (no timezone shift, unlike new Date(iso)). */
export function formatTourDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return y && m && d ? `${d}/${m}/${y}` : isoDate;
}

/** Short human code from an id like "res_1760000000000_k3x9a" → "K3X9A". */
export function reservationCode(id: string): string {
  return (id.split('_').pop() || id).slice(-6).toUpperCase();
}

export function itemTotal(item: CartItem): number {
  return item.adults * item.adultPrice + item.children * item.childPrice + item.babies * item.babyPrice;
}

export interface WhatsAppReservationInfo {
  items: CartItem[];
  totalPrice: number;
  deposit: number;
  remaining: number;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  reservationId?: string;
  /** e.g. "Sinal pago via Pix (pagamento #123)" — omitted when nothing was paid yet */
  paymentNote?: string;
  /** Lead guest document, shown after the contact lines. */
  documentType?: 'cpf' | 'passport';
  documentNumber?: string;
  /** Passport country already written in Portuguese, e.g. "Argentina". */
  documentCountry?: string;
  /** Tour booked on WhatsApp only: no deposit lines in the message. */
  whatsappOnly?: boolean;
  /** Jeep tours: where the guide picks the guest up (or the agency). */
  pickupPlace?: string;
}

export function buildReservationMessage(info: WhatsAppReservationInfo): string {
  const lines: string[] = ['Olá! Quero reservar pelo site AQUAMARINA TOURS.', ''];

  info.items.forEach((item, index) => {
    if (info.items.length > 1) lines.push(`*Passeio ${index + 1}*`);
    lines.push(`🚤 *Passeio:* ${item.title}`);
    lines.push(`📅 *Data:* ${formatTourDate(item.date)}`);
    lines.push(`👤 *Adultos (10+ anos):* ${item.adults}`);
    lines.push(`🧒 *Crianças (5 a 9 anos):* ${item.children}`);
    lines.push(`👶 *Bebês (0 a 4 anos):* ${item.babies}`);
    lines.push(`💰 *Valor:* ${money(itemTotal(item))}`);
    lines.push('');
  });

  lines.push(`*Total:* ${money(info.totalPrice)}`);
  if (info.whatsappOnly) {
    lines.push('📲 *Reserva pelo WhatsApp* (sem pagamento pelo site)');
  } else {
    lines.push(`*Sinal via Pix:* ${money(info.deposit)}`);
    lines.push(`*Restante no dia:* ${money(info.remaining)}`);
  }
  if (info.paymentNote) lines.push(`✅ ${info.paymentNote}`);

  const contact = [
    info.customerName && `*Responsável:* ${info.customerName}`,
    info.customerPhone && `*WhatsApp:* ${info.customerPhone}`,
    info.customerEmail && `*Email:* ${info.customerEmail}`,
    info.documentType && info.documentNumber && (info.documentType === 'cpf'
      ? `*CPF:* ${info.documentNumber}`
      : `*Passaporte:* ${info.documentNumber}${info.documentCountry ? ` (${info.documentCountry})` : ''}`),
    info.pickupPlace && `📍 *Busca:* ${info.pickupPlace}`,
    info.reservationId && `*Código:* #${reservationCode(info.reservationId)}`,
  ].filter(Boolean) as string[];
  if (contact.length) lines.push('', ...contact);

  return lines.join('\n');
}

export function reservationWhatsAppUrl(info: WhatsAppReservationInfo): string {
  return `https://wa.me/${BUSINESS_WHATSAPP}?text=${encodeURIComponent(buildReservationMessage(info))}`;
}

export interface WhatsAppQuoteInfo {
  /** The tour's name as the team knows it (Portuguese). */
  title: string;
  /** ISO date, e.g. "2027-01-23". */
  date: string;
  adults: number;
  children: number;
  babies: number;
  /** Whole-boat or group price: only the head count matters, not the ages. */
  headCountOnly: boolean;
  customerName: string;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** Quote request for a tour booked on WhatsApp: the tour, the date, how many people and who asks. */
export function buildQuoteMessage(info: WhatsAppQuoteInfo): string {
  const people = info.adults + info.children + info.babies;
  const groups = [
    info.adults > 0 && plural(info.adults, 'adulto', 'adultos'),
    info.children > 0 && plural(info.children, 'criança', 'crianças'),
    info.babies > 0 && plural(info.babies, 'bebê', 'bebês'),
  ].filter(Boolean);
  return [
    'Olá! Gostaria de uma cotação pelo site AQUAMARINA TOURS.',
    '',
    `🚤 *Passeio:* ${info.title}`,
    `📅 *Data:* ${formatTourDate(info.date)}`,
    `👥 *Pessoas:* ${info.headCountOnly || groups.length < 2 ? people : `${people} (${groups.join(', ')})`}`,
    `👤 *Nome:* ${info.customerName}`,
  ].join('\n');
}

export function quoteWhatsAppUrl(info: WhatsAppQuoteInfo): string {
  return `https://wa.me/${BUSINESS_WHATSAPP}?text=${encodeURIComponent(buildQuoteMessage(info))}`;
}

/**
 * Opens WhatsApp exactly once: in a new tab, or in this one when the browser blocks the new tab.
 * (window.open with 'noopener' always returns null, so it can't tell a blocked tab from an open one: asking for
 * it opened WhatsApp twice, and a phone with two WhatsApp accounts sent the booking from both.)
 */
export function openWhatsApp(url: string) {
  const opened = window.open(url, '_blank');
  if (opened) opened.opener = null;
  else window.location.href = url;
}
